import { useMemo, useState } from 'react';
import { FREE_PLANT, SPRING_PLANT, cloneConfig, getPreset } from '../../content/presets';
import { ScalarReferenceEngine } from '../../simulation/engine';
import { analyzeDamping, forceComponents } from '../../simulation/model';
import { Simulation } from '../../simulation/simulation';
import type { PlantParams } from '../../simulation/types';
import { simClient } from '../../simulation/client';
import { useApp } from '../../state/store';
import { MathTex } from '../MathTex';
import { NumberField } from '../sim/NumberField';
import { Term } from '../Term';

const n = (x: number, d = 2) => {
  const v = Number(x.toFixed(d));
  return (Object.is(v, -0) ? 0 : v).toFixed(d);
};
const signed = (x: number, d = 2) => (x >= 0 ? `+${n(x, d)}` : `−${n(-x, d)}`);

function useSnapshot() {
  const experiment = useApp((s) => s.experiment);
  return () => {
    const s = simClient.getSnapshot().latest;
    return s && experiment ? { sample: s, plant: experiment.config.plant } : null;
  };
}

// ── Derivative as a rate ─────────────────────────────────────────────
export function DerivativeRate() {
  const [z1, setZ1] = useState(2);
  const [z2, setZ2] = useState(2.01);
  const [dt, setDt] = useState(0.01);
  const rate = (z2 - z1) / dt;
  return (
    <div className="widget" role="group" aria-label="Rate calculator">
      <p className="widget-title">Try it: a rate from two readings</p>
      <div className="grid-3">
        <NumberField name="First altitude" label="first altitude" unit="m" value={z1} min={0} max={5} step={0.001} onCommit={setZ1} onLive={setZ1} />
        <NumberField name="Second altitude" label="second altitude" unit="m" value={z2} min={0} max={5} step={0.001} onCommit={setZ2} onLive={setZ2} />
        <NumberField name="Time between readings" label="time between" unit="s" value={dt} min={0.001} max={2} step={0.001} onCommit={setDt} onLive={setDt} />
      </div>
      <p className="calc">
        <MathTex tex={`\\text{rate} \\approx \\frac{${n(z2, 3)} - ${n(z1, 3)}}{${n(dt, 3)}} = ${n(rate, 3)}\\ \\text{m/s}`} />
      </p>
      <p className="small">
        The shorter the time between readings, the closer this gets to the true instantaneous rate, the derivative <MathTex tex="\dot z" />. That is why
        the simulator uses tiny steps.
      </p>
    </div>
  );
}

// ── Force balance ────────────────────────────────────────────────────
export function ForceBalance() {
  const snapshot = useSnapshot();
  const [plant, setPlant] = useState<PlantParams>(SPRING_PLANT);
  const [z, setZ] = useState(2.5);
  const [v, setV] = useState(0.4);
  const [T, setT] = useState(12);
  const [d, setD] = useState(0);
  const f = forceComponents(plant, z, v, T, d);
  const a = f.net / plant.m;
  const setHighlight = useApp((s) => s.setHighlight);
  const rows = [
    { id: 'T', name: 'Thrust', formula: 'T', value: f.thrust, hl: 'force:thrust' as const },
    { id: 'weight', name: 'Gravity', formula: `-m\\,g = -${n(plant.m)} \\times ${n(plant.g)}`, value: f.gravity, hl: 'force:gravity' as const },
    { id: 'b', name: 'Damping', formula: `-b\\,v = -${n(plant.b)} \\times (${n(v)})`, value: f.damping, hl: 'force:damping' as const },
    { id: 'k', name: 'Spring', formula: `-k\\,(z - z_s) = -${n(plant.k)} \\times (${n(z)} - ${n(plant.zs)})`, value: f.spring, hl: 'force:spring' as const },
    { id: 'disturbance', name: 'Disturbance', formula: 'd', value: f.disturbance, hl: 'force:disturbance' as const },
  ];
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.value)), Math.abs(f.net));
  const hoverHl = (hl: (typeof rows)[number]['hl']) => {
    if (useApp.getState().highlightSticky) return;
    setHighlight(hl);
  };
  return (
    <div className="widget" role="group" aria-label="Force balance calculator">
      <p className="widget-title">Force balance with numbers</p>
      <div className="row gap-s wrap">
        <button
          type="button"
          className="btn small"
          onClick={() => {
            const s = snapshot();
            if (!s) return;
            setPlant(s.plant);
            setZ(+s.sample.z.toFixed(3));
            setV(+s.sample.v.toFixed(3));
            setT(+s.sample.T.toFixed(3));
            setD(+s.sample.d.toFixed(3));
          }}
        >
          Copy current simulator snapshot
        </button>
        <span className="small muted">
          Plant: m = {plant.m} kg, g = {plant.g}, b = {plant.b}, k = {plant.k}, z_s = {plant.zs}
        </span>
      </div>
      <div className="grid-2">
        <NumberField name="Altitude z" label={<Term id="z">altitude z</Term>} unit="m" value={z} min={0} max={6} step={0.01} onCommit={setZ} onLive={setZ} />
        <NumberField name="Velocity v" label={<Term id="v">velocity v</Term>} unit="m/s" value={v} min={-3} max={3} step={0.01} onCommit={setV} onLive={setV} />
        <NumberField name="Thrust T" label={<Term id="T">thrust T</Term>} unit="N" value={T} min={0} max={25} step={0.01} onCommit={setT} onLive={setT} />
        <NumberField name="Disturbance d" label={<Term id="disturbance">disturbance d</Term>} unit="N" value={d} min={-5} max={5} step={0.1} onCommit={setD} onLive={setD} />
      </div>
      <table className="forces">
        <caption className="sr-only">Forces on the vehicle, upward positive</caption>
        <thead>
          <tr>
            <th scope="col">Force</th>
            <th scope="col">Formula</th>
            <th scope="col">Value (N)</th>
            <th scope="col" aria-hidden="true"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onMouseEnter={() => hoverHl(r.hl)} onFocus={() => hoverHl(r.hl)}>
              <th scope="row">{r.name}</th>
              <td>
                <MathTex tex={r.formula} />
              </td>
              <td className="num-cell">{signed(r.value)}</td>
              <td aria-hidden="true" className="bar-cell">
                <span className={`bar ${r.value >= 0 ? 'up' : 'down'}`} style={{ width: `${(Math.abs(r.value) / max) * 100}%` }} />
              </td>
            </tr>
          ))}
          <tr className="total">
            <th scope="row">Net force</th>
            <td>sum of the above</td>
            <td className="num-cell">{signed(f.net)}</td>
            <td aria-hidden="true" className="bar-cell">
              <span className={`bar ${f.net >= 0 ? 'up' : 'down'}`} style={{ width: `${(Math.abs(f.net) / max) * 100}%` }} />
            </td>
          </tr>
        </tbody>
      </table>
      <p className="calc" aria-live="polite">
        <MathTex tex={`a = \\frac{F_{net}}{m} = \\frac{${n(f.net)}}{${n(plant.m)}} = ${n(a)}\\ \\text{m/s}^2`} />{' '}
        {Math.abs(a) < 0.005 ? '(balanced: no acceleration)' : a > 0 ? '(accelerating upward)' : '(accelerating downward)'}
      </p>
    </div>
  );
}

// ── Euler step ───────────────────────────────────────────────────────
export function EulerStep() {
  const snapshot = useSnapshot();
  const [plant, setPlant] = useState<PlantParams>(SPRING_PLANT);
  const [z, setZ] = useState(2.5);
  const [v, setV] = useState(0);
  const [T, setT] = useState(12);
  const [dt, setDt] = useState(0.05);
  const a = forceComponents(plant, z, v, T, 0).net / plant.m;
  const zE = z + v * dt;
  const vE = v + a * dt;
  const exact = useMemo(() => {
    const e = new ScalarReferenceEngine();
    const sub = Math.max(1, Math.round(dt / 0.0005));
    e.configure(plant, dt / sub);
    e.setState(z, v);
    for (let i = 0; i < sub; i++) e.step(T, 0);
    return { z: e.z, v: e.v };
  }, [plant, z, v, T, dt]);
  return (
    <div className="widget" role="group" aria-label="Euler step calculator">
      <p className="widget-title">One step forward: new ≈ old + rate × Δt</p>
      <div className="row gap-s wrap">
        <button
          type="button"
          className="btn small"
          onClick={() => {
            const s = snapshot();
            if (!s) return;
            setPlant(s.plant);
            setZ(+s.sample.z.toFixed(3));
            setV(+s.sample.v.toFixed(3));
            setT(+s.sample.T.toFixed(3));
          }}
        >
          Copy current simulator snapshot
        </button>
      </div>
      <div className="grid-2">
        <NumberField name="Altitude z" label="z" unit="m" value={z} min={0} max={6} step={0.01} onCommit={setZ} onLive={setZ} />
        <NumberField name="Velocity v" label="v" unit="m/s" value={v} min={-3} max={3} step={0.01} onCommit={setV} onLive={setV} />
        <NumberField name="Thrust T" label="T" unit="N" value={T} min={0} max={25} step={0.01} onCommit={setT} onLive={setT} />
        <NumberField name="Time step" label={<Term id="dt">Δt</Term>} unit="s" value={dt} min={0.005} max={1} step={0.005} onCommit={setDt} onLive={setDt} />
      </div>
      <ol className="calc-steps">
        <li>
          Acceleration from the force balance: <MathTex tex={`a = ${n(a, 3)}\\ \\text{m/s}^2`} />
        </li>
        <li>
          <MathTex tex={`z_{new} \\approx z + v\\,\\Delta t = ${n(z, 3)} + (${n(v, 3)})(${n(dt, 3)}) = ${n(zE, 4)}\\ \\text{m}`} />
        </li>
        <li>
          <MathTex tex={`v_{new} \\approx v + a\\,\\Delta t = ${n(v, 3)} + (${n(a, 3)})(${n(dt, 3)}) = ${n(vE, 4)}\\ \\text{m/s}`} />
        </li>
      </ol>
      <p className="small">
        Accurate method (RK4, many tiny sub-steps): z = {n(exact.z, 4)} m, v = {n(exact.v, 4)} m/s. The Euler guess is off by {n(Math.abs(zE - exact.z) * 1000, 2)} mm and{' '}
        {n(Math.abs(vE - exact.v) * 1000, 2)} mm/s. Try a bigger Δt to see the approximation get worse.
      </p>
    </div>
  );
}

// ── Matrix rows ──────────────────────────────────────────────────────
export function MatrixRows() {
  const [free, setFree] = useState(false);
  const plant = free ? FREE_PLANT : SPRING_PLANT;
  const [q, setQ] = useState(0.3);
  const [v, setV] = useState(-0.2);
  const [u, setU] = useState(0.5);
  const [d, setD] = useState(0);
  const [open, setOpen] = useState<0 | 1 | 2>(0);
  const { k, b, m } = plant;
  const r1 = 0 * q + 1 * v + 0 * u + 0 * d;
  const r2 = (-k / m) * q + (-b / m) * v + (1 / m) * u + (1 / m) * d;
  return (
    <div className="widget" role="group" aria-label="Matrix row expansion">
      <p className="widget-title">Expand the matrix equation row by row</p>
      <label className="check small">
        <input type="checkbox" checked={free} onChange={(e) => setFree(e.target.checked)} /> Free-altitude preset (k = 0)
      </label>
      <div className="grid-2">
        <NumberField name="Deviation q" label={<Term id="q">q</Term>} unit="m" value={q} min={-1} max={1} step={0.01} onCommit={setQ} onLive={setQ} />
        <NumberField name="Velocity v" label={<Term id="v">v</Term>} unit="m/s" value={v} min={-2} max={2} step={0.01} onCommit={setV} onLive={setV} />
        <NumberField name="Thrust deviation u" label={<Term id="u">u</Term>} unit="N" value={u} min={-5} max={5} step={0.1} onCommit={setU} onLive={setU} />
        <NumberField name="Disturbance d" label={<Term id="disturbance">d</Term>} unit="N" value={d} min={-5} max={5} step={0.1} onCommit={setD} onLive={setD} />
      </div>
      <div className="calc">
        <MathTex
          display
          tex={`\\begin{bmatrix}\\dot q\\\\ \\dot v\\end{bmatrix} = \\begin{bmatrix}0 & 1\\\\ ${n(-k / m)} & ${n(-b / m)}\\end{bmatrix}\\begin{bmatrix}${n(q)}\\\\ ${n(v)}\\end{bmatrix} + \\begin{bmatrix}0\\\\ ${n(1 / m)}\\end{bmatrix}(${n(u)}) + \\begin{bmatrix}0\\\\ ${n(1 / m)}\\end{bmatrix}(${n(d)})`}
        />
      </div>
      <div className="row gap-s wrap" role="group" aria-label="Choose a row to expand">
        <button type="button" className={`btn small${open === 1 ? ' primary' : ''}`} aria-pressed={open === 1} onClick={() => setOpen(open === 1 ? 0 : 1)}>
          Expand row 1
        </button>
        <button type="button" className={`btn small${open === 2 ? ' primary' : ''}`} aria-pressed={open === 2} onClick={() => setOpen(open === 2 ? 0 : 2)}>
          Expand row 2
        </button>
      </div>
      {open === 1 && (
        <div className="expand" aria-live="polite">
          <MathTex display tex={`\\dot q = 0\\cdot(${n(q)}) + 1\\cdot(${n(v)}) + 0\\cdot(${n(u)}) + 0\\cdot(${n(d)}) = ${n(r1)}\\ \\text{m/s}`} />
          <p className="small">
            Row 1 says: "the rate of change of q is v." Thrust and disturbance do not change position directly; they change velocity first.
          </p>
        </div>
      )}
      {open === 2 && (
        <div className="expand" aria-live="polite">
          <MathTex
            display
            tex={`\\dot v = (${n(-k / m)})(${n(q)}) + (${n(-b / m)})(${n(v)}) + (${n(1 / m)})(${n(u)}) + (${n(1 / m)})(${n(d)}) = ${n(r2, 3)}\\ \\text{m/s}^2`}
          />
          <p className="small">
            Row 2 says: "acceleration = spring term + damping term + thrust-deviation term + disturbance term, each divided by mass." It is the force balance
            again.
          </p>
        </div>
      )}
      <details className="small">
        <summary>Text version of this equation</summary>
        <ol>
          <li>
            First row: q-rate = 0 × q + 1 × v + 0 × u + 0 × d = {n(r1)} m/s.
          </li>
          <li>
            Second row: v-rate = ({n(-k / m)}) × q + ({n(-b / m)}) × v + ({n(1 / m)}) × u + ({n(1 / m)}) × d = {n(r2, 3)} m/s².
          </li>
          <li>Output: y = 1 × q + 0 × v = {n(q)} m, so the displayed altitude is 2 + {n(q)} = {n(2 + q)} m.</li>
        </ol>
      </details>
    </div>
  );
}

// ── Damping calculator ───────────────────────────────────────────────
export function DampingCalculator() {
  const [m, setM] = useState(1);
  const [k, setK] = useState(4);
  const [b, setB] = useState(1);
  const [Kp, setKp] = useState(0);
  const [Kd, setKd] = useState(0);
  const cfg = useMemo(() => {
    const c = cloneConfig(getPreset('damping-release').config);
    c.plant = { ...c.plant, m, k, b };
    c.controller = { ...c.controller, kind: Kp || Kd ? 'pid' : 'open-loop', Kp, Ki: 0, Kd };
    return c;
  }, [m, k, b, Kp, Kd]);
  const a = analyzeDamping(cfg);
  const trace = useMemo(() => {
    try {
      const sim = new Simulation(new ScalarReferenceEngine(), cfg);
      sim.runToEnd();
      return sim.trace;
    } catch {
      return [];
    }
  }, [cfg]);
  const W = 300;
  const H = 100;
  const path = trace
    .filter((_, i) => i % 4 === 0)
    .map((s, i) => `${i ? 'L' : 'M'}${((s.t / cfg.duration) * W).toFixed(1)},${(H / 2 - ((s.z - 2) / 0.3) * (H / 2 - 4)).toFixed(1)}`)
    .join('');
  return (
    <div className="widget" role="group" aria-label="Damping calculator">
      <p className="widget-title">Damping calculator (release from +0.25 m)</p>
      <div className="grid-3">
        <NumberField name="Mass m" label="m" unit="kg" value={m} min={0.2} max={3} step={0.1} onCommit={setM} onLive={setM} />
        <NumberField name="Spring k" label="k" unit="N/m" value={k} min={0} max={10} step={0.5} onCommit={setK} onLive={setK} />
        <NumberField name="Damping b" label="b" unit="N·s/m" value={b} min={0} max={12} step={0.5} onCommit={setB} onLive={setB} />
        <NumberField name="K_p" label="K_p" unit="N/m" value={Kp} min={0} max={40} step={1} onCommit={setKp} onLive={setKp} />
        <NumberField name="K_d" label="K_d" unit="N·s/m" value={Kd} min={0} max={20} step={0.5} onCommit={setKd} onLive={setKd} />
      </div>
      <p className="calc" aria-live="polite">
        {a.zeta === null ? (
          <>ζ is undefined here: {a.notes[0]}</>
        ) : (
          <>
            <MathTex tex={`\\omega_n = \\sqrt{\\tfrac{${n(k + Kp)}}{${n(m)}}} = ${n(a.wn!)}\\ \\text{rad/s},\\quad \\zeta = \\frac{${n(b + Kd)}}{2\\sqrt{${n(m)}\\cdot ${n(k + Kp)}}} = ${n(a.zeta)}`} />{' '}
            → <strong>{a.regime}</strong>
          </>
        )}
      </p>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Release response: lowest altitude ${n(Math.min(...trace.map((s) => s.z)), 3)} m.`} className="mini-plot">
        <rect x={0} y={0} width={W} height={H} className="plot-bg" />
        <line x1={0} x2={W} y1={H / 2} y2={H / 2} className="ref-line" />
        <path d={path} fill="none" stroke="var(--c-z)" strokeWidth={2} />
      </svg>
      <p className="hint">Dashed line: rest altitude. 10 s shown. Uses the same simulation code as the lab (scalar integrator).</p>
    </div>
  );
}
